import { useFetch2 } from "../util";

export interface FindDialogsOutput {
  id: number;
  name: string;
  lastMessageDateTime: string;
  lastMessage: string;
  unreadMessageTotal: number;
  plataform: string;
  avatarUrl?: string;
  fixedAt?: string | null;
}

export interface FindDialogsPageableOutput {
  id: number;
  name: string;
  lastMessageDateTime: string;
  lastMessage: string;
  unreadMessageTotal: number;
  plataform: string;
  avatarUrl?: string;
  fixedAt?: string | null;
}

export interface FindQueueDialogsPageableOutput {
  id: number;
  name: string;
  lastMessageDateTime: string;
  lastMessage: string;
  unreadMessageTotal: number;
  plataform: string;
  avatarUrl?: string;
}

export interface PageableDialogsResponse<T> {
  result: T[];
  total: number;
}

export interface JoinQueueDialogOutput {
  dialogId: number;
}

export interface FixDialogOutput {
  dialogId: number;
  fixedAt: string;
}

export interface iFile {
  id: string;
  fileName: string;
  url: string;
  mime: string;
}

interface Buttons2 {
  id: number;
  position: number;
  label: string;
  type: string;
  link: string;
}

interface Button {
  id: number;
  position: number;
  label: string;
  type: string;
  link?: string;
}
export interface Message {
  id: number;
  message: string;
  sender: string;
  dateTime: string;
  file: string;
  buttons: (Button | Buttons2)[];
  files: iFile[];
  updatedAt?: string;
  status?: string;
  forwardedMessage?: boolean;
  parentId: number;
  quotedMessage?: {
    parentId: number;
    message: string;
    mime?: string;
  };
  clientTempId?: string;
  sendState?: "sending" | "failed";
  errorText?: string;
  retryable?: boolean;
  retryPayload?: {
    message: string;
    cannedMessageId?: number;
    fileId?: number;
    files?: iFile[];
    quotedMessageId?: number;
    forwardMessageId?: number;
    quotedMessage?: {
      parentId: number;
      message: string;
      mime?: string;
    };
  };
  sourceDialogId?: number;
  sourceDialogDateTime?: string;
  sourceDialogName?: string;
  sourceBotName?: string;
  sourceUserName?: string;
}

export const compareMessagesByDateTimeAndId = (
  a: Pick<Message, "id" | "dateTime">,
  b: Pick<Message, "id" | "dateTime">
) => {
  const dateDiff =
    new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime();

  if (dateDiff !== 0) {
    return dateDiff;
  }

  return a.id - b.id;
};

export const normalizeHistoryMessage = (
  message: Message,
  dialogInfo: Pick<iDialogMessagesFile, "id" | "dateTime" | "name" | "botName">
): Message => ({
  ...message,
  sourceDialogId: message.sourceDialogId ?? dialogInfo.id,
  sourceDialogDateTime: message.sourceDialogDateTime ?? dialogInfo.dateTime,
  sourceDialogName: message.sourceDialogName ?? dialogInfo.name,
  sourceBotName: message.sourceBotName ?? dialogInfo.botName
});

export const normalizeHistoryMessages = (
  messages: Message[],
  dialogInfo: Pick<iDialogMessagesFile, "id" | "dateTime" | "name" | "botName">
) =>
  messages
    .map((message) => normalizeHistoryMessage(message, dialogInfo))
    .sort(compareMessagesByDateTimeAndId);
export interface iDialogMessagesFile {
  id: number;
  botId: number;
  name: string;
  botName: string;
  dateTime?: string;
  closedAt?: string;
  platform: string;
  messages: Message[];
  status: string;
  token: string;
  phone: string;
  transferredToThirdpartySystem: boolean;
  transferred: boolean;
  workspaceTransfer: boolean;
  userTransferredId: number;
  whatsappQrCode?: boolean;
  qrCodeConnected?: boolean;
  avatarUrl?: string;
  pinnedMessages: PinnedMessage[];
}

export interface PinnedMessage {
  dialogId: number;
  messageId: number;
  message: string;
  mime?: string;
  sender: string;
  dateTime: string;
}

export interface FindBotsList {
  id: number;
  name: string;
}

export interface EditMessage {
  dialogId: number;
  messageId: number;
  editedMessage: string;
}

const useOmnichannel = () => {
  const request = useFetch2();

  const findUnreadRooms = async (data?: {
    workspace?: number;
    name?: string;
    status?: string;
  }): Promise<number> => {
    const params = new URLSearchParams();

    if (data?.workspace) {
      params.append("workspace", `${data.workspace}`);
    }

    if (data?.name) {
      params.append("name", `${data.name}`);
    }

    if (data?.status) {
      params.append("status", `${data.status}`);
    }

    return request(`/api/omnichannel/unreadRooms?${params}`, {
      method: "GET"
    });
  };

  const findDialogs = async (data?: {
    workspace?: number;
    bot?: number;
    name?: string;
    status?: string;
  }): Promise<FindDialogsOutput[]> => {
    const params = new URLSearchParams();

    if (data?.workspace) {
      params.append("workspace", `${data.workspace}`);
    }

    if (data?.name) {
      params.append("name", `${data.name}`);
    }

    if (data?.status) {
      params.append("status", `${data.status}`);
    }

    if (data?.bot && data?.bot > 0) {
      params.append("bots", `${data.bot}`);
    }

    return request(`/api/omnichannel/dialogs?${params}`, {
      method: "GET"
    });
  };

  const findDialogsPageable = async (data?: {
    workspace?: number;
    bot?: number;
    limit?: number;
    offset?: number;
    name?: string;
    status?: string;
  }): Promise<PageableDialogsResponse<FindDialogsPageableOutput>> => {
    const params = new URLSearchParams();

    if (data?.workspace) {
      params.append("workspace", `${data.workspace}`);
    }

    if (data?.name) {
      params.append("name", `${data.name}`);
    }

    if (data?.status) {
      params.append("status", `${data.status}`);
    }

    if (data?.bot && data?.bot > 0) {
      params.append("bots", `${data.bot}`);
    }

    if (data?.limit) {
      params.append("limit", `${data.limit}`);
    }

    if (data?.offset) {
      params.append("offset", `${data.offset}`);
    } else {
      params.append("offset", `${0}`);
    }

    return request(`/api/omnichannel/dialogs-pageable?${params}`, {
      method: "GET"
    });
  };

  const findQueueRooms = async (data?: {
    workspace?: number;
    name?: string;
  }): Promise<number> => {
    const params = new URLSearchParams();

    if (data?.workspace) {
      params.append("workspace", `${data.workspace}`);
    }

    if (data?.name) {
      params.append("name", `${data.name}`);
    }

    return request(`/api/omnichannel/queue/count?${params}`, {
      method: "GET"
    });
  };

  const findQueueDialogsPageable = async (data?: {
    workspace?: number;
    limit?: number;
    offset?: number;
    name?: string;
  }): Promise<PageableDialogsResponse<FindQueueDialogsPageableOutput>> => {
    const params = new URLSearchParams();

    if (data?.workspace) {
      params.append("workspace", `${data.workspace}`);
    }

    if (data?.name) {
      params.append("name", `${data.name}`);
    }

    if (data?.limit) {
      params.append("limit", `${data.limit}`);
    }

    if (data?.offset) {
      params.append("offset", `${data.offset}`);
    } else {
      params.append("offset", `${0}`);
    }

    return request(`/api/omnichannel/queue/dialogs-pageable?${params}`, {
      method: "GET"
    });
  };

  const joinQueueDialog = async (
    dialogId: number
  ): Promise<JoinQueueDialogOutput> => {
    return request(`/api/omnichannel/queue/dialogs/${dialogId}/join`, {
      method: "POST"
    });
  };

  const findBotsList = async (data: {
    workspaces?: number[];
    companyId?: number;
  }): Promise<{ result: FindBotsList[] }> => {
    const body = {
      companyId: data.companyId,
      workspaces: data.workspaces ?? [],
      offset: 0,
      limit: 100
    };

    return request(
      `/api/bots/list`,
      {
        method: "POST"
      },
      {
        ...body
      }
    );
  };

  const markAsRead = async (dialogId: number) => {
    return request(`/api/omnichannel/dialogs/markAsRead/${dialogId}`, {
      method: "POST"
    });
  };

  const markAsUnread = async (dialogId: number) => {
    return request(`/api/omnichannel/dialogs/markAsUnread/${dialogId}`, {
      method: "POST"
    });
  };

  const fixDialog = async (dialogId: number): Promise<FixDialogOutput> => {
    return request(`/api/omnichannel/dialogs/fix/${dialogId}`, {
      method: "POST"
    });
  };

  const unfixDialog = async (dialogId: number) => {
    return request(`/api/omnichannel/dialogs/unfix/${dialogId}`, {
      method: "POST"
    });
  };

  const pinMessage = async (dialogId: number, messageId: number) => {
    return request(`/api/omnichannel/dialogs/${dialogId}/pin/${messageId}`, {
      method: "POST"
    });
  };

  const unpinMessage = async (dialogId: number) => {
    return request(`/api/omnichannel/dialogs/${dialogId}/pin`, {
      method: "DELETE"
    });
  };

  const finalize = async (dialogId: number) => {
    return request(`/api/omnichannel/dialogs/finalize/${dialogId}`, {
      method: "POST"
    });
  };

  const editMessage = async (data: EditMessage) => {
    return request(
      `/api/omnichannel/dialogs/messages/${data.messageId}`,
      { method: "PATCH" },
      {
        dialogId: data.dialogId,
        editedMessage: data.editedMessage
      }
    );
  };

  return {
    findUnreadRooms,
    findQueueRooms,
    findDialogs,
    findDialogsPageable,
    findQueueDialogsPageable,
    joinQueueDialog,
    markAsRead,
    markAsUnread,
    fixDialog,
    unfixDialog,
    pinMessage,
    unpinMessage,
    finalize,
    findBotsList,
    editMessage
  };
};

export default useOmnichannel;
